import { Task } from "@common/types/task";
import { WorkspaceMember } from "@common/types/workspace";
import { WORKSPACE_ADMIN_ROLES } from "@common/constants";

/**
 * Reason a (non-org) workspace member can or cannot see a task, mirroring the
 * backend's `getTaskVisibility` result. The shape is richer than a boolean so
 * the UI can explain *why* a client has access (or is hidden).
 */
type TaskVisibilityInput = Pick<
    Task,
    "organizationId" | "entityIds" | "createdById" | "assigneeId"
>;

export type ClientVisibilityReason =
    | {
          visible: true;
          reason: "admin" | "assignee" | "creator" | "all_entities";
      }
    | {
          visible: false;
          reason:
              | "has_organization"
              | "missing_entities"
              | "not_creator_or_assignee";
      };

/**
 * Determines whether a given workspace member whose `organizationId` is
 * `null` (i.e. a "client") would be able to see a task under the same
 * access-control rules enforced by the backend's `getTaskVisibility` /
 * `applyTaskVisibilityFilters`.
 *
 * **IMPORTANT:** This logic must stay in sync with:
 *   - `backend/src/modules/task/helpers/task-entity-access.helper.ts`
 *   - `backend/src/modules/task/services/internal/task-query.internal.service.ts`
 *
 * Rules applied (same order as the backend):
 *
 *   Rule A – Org isolation
 *     All non-org users (including admins): hide tasks where
 *     `task.organizationId != null` unless the user is the assignee
 *     (assignee-override). Admin status does NOT bypass org isolation.
 *
 *   Rule B – Admin bypass
 *     Workspace admins (PRIMARY_ADMIN / ADMIN) that pass Rule A see all
 *     tasks without any further creator/assignee or entity restriction.
 *
 *   Rule C – Non-admin visibility
 *     • Task HAS entity assignments → visible if the member has access to
 *       ALL of those entities (`assignToNewEntities` counts as having access
 *       to every entity).
 *     • Task has NO entity assignments → visible only if the member is the
 *       creator or the assignee.
 */
export function getTaskClientVisibility(
    task: TaskVisibilityInput,
    member: WorkspaceMember,
): ClientVisibilityReason {
    if (member.organizationId !== null) {
        // This helper is only meaningful for clients (non-org members). Other
        // callers shouldn't invoke it, but if they do we err on the side of
        // hidden with a best-effort reason.
        return { visible: false, reason: "has_organization" };
    }

    const isAdmin = WORKSPACE_ADMIN_ROLES.some((r) => r === member.role);

    // Rule A: org isolation applies to ALL non-org users, including admins.
    if (task.organizationId !== null && task.assigneeId !== member.user.id) {
        return { visible: false, reason: "has_organization" };
    }

    // Rule B: admin bypass (only reached when Rule A passes).
    if (isAdmin) {
        return { visible: true, reason: "admin" };
    }

    const taskEntityIds = task.entityIds ?? [];
    const hasEntities = taskEntityIds.length > 0;

    if (hasEntities) {
        const memberEntityIds = new Set(
            member.entityAssignments.map((ea) => ea.entity.id),
        );
        const hasAll = taskEntityIds.every((id) => memberEntityIds.has(id));
        return hasAll
            ? { visible: true, reason: "all_entities" }
            : { visible: false, reason: "missing_entities" };
    }

    // Rule C (no entities): must be creator or assignee.
    if (task.createdById === member.user.id) {
        return { visible: true, reason: "creator" };
    }
    if (task.assigneeId === member.user.id) {
        return { visible: true, reason: "assignee" };
    }
    return { visible: false, reason: "not_creator_or_assignee" };
}

/**
 * Reason a workspace member (any member — org user, client, or admin) can or
 * cannot see a task. Superset of {@link ClientVisibilityReason} — adds an
 * `"org_mismatch"` denial for org users whose org differs from the task's.
 *
 * Mirrors the backend's `getTaskVisibility` rules:
 *   Rule A (org isolation) → Rule B (admin bypass) → Rule C (entities /
 *   creator-assignee).
 */
export type MemberVisibilityReason =
    | {
          visible: true;
          reason: "admin" | "assignee" | "creator" | "all_entities";
      }
    | {
          visible: false;
          reason:
              | "org_mismatch"
              | "has_organization"
              | "missing_entities"
              | "not_creator_or_assignee";
      };

/**
 * Determines whether the given workspace member would be able to see the
 * task under the backend's unified visibility rules.
 *
 * **IMPORTANT:** stays in sync with
 * `backend/src/modules/task/helpers/task-entity-access.helper.ts` and
 * `backend/src/modules/task/services/internal/task-query.internal.service.ts`.
 */
export function getTaskMemberVisibility(
    task: TaskVisibilityInput,
    member: WorkspaceMember,
): MemberVisibilityReason {
    const isAdmin = WORKSPACE_ADMIN_ROLES.some((r) => r === member.role);

    // Rule A: org isolation.
    if (member.organizationId !== null) {
        // Org user: must share the task's org or task must have null org.
        if (
            task.organizationId !== null &&
            task.organizationId !== member.organizationId
        ) {
            return { visible: false, reason: "org_mismatch" };
        }
    } else {
        // Non-org user (including admins): org isolation applies regardless of
        // admin status. Only the assignee-override allows seeing an org task.
        if (
            task.organizationId !== null &&
            task.assigneeId !== member.user.id
        ) {
            return { visible: false, reason: "has_organization" };
        }
    }

    // Rule B: admin bypass (only after passing Rule A).
    if (isAdmin) {
        return { visible: true, reason: "admin" };
    }

    const taskEntityIds = task.entityIds ?? [];
    const hasEntities = taskEntityIds.length > 0;

    if (hasEntities) {
        const memberEntityIds = new Set(
            member.entityAssignments.map((ea) => ea.entity.id),
        );
        const hasAll = taskEntityIds.every((id) => memberEntityIds.has(id));
        return hasAll
            ? { visible: true, reason: "all_entities" }
            : { visible: false, reason: "missing_entities" };
    }

    if (task.createdById === member.user.id) {
        return { visible: true, reason: "creator" };
    }
    if (task.assigneeId === member.user.id) {
        return { visible: true, reason: "assignee" };
    }
    return { visible: false, reason: "not_creator_or_assignee" };
}
